import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import Swal from "sweetalert2";
import {
  PresupuestoVersionesGet,
  PresupuestoVersionCrear,
  PresupuestoVersionRestaurar,
} from "../../service/presupuestoVersiones";

/**
 * Historial de versiones del presupuesto de un proyecto.
 *
 * Crear una versión copia las cuentas/subcuentas/conceptos de la activa a una
 * versión nueva, que queda como la activa; la anterior se guarda tal cual,
 * con su historial de pagos y facturas. Usar una versión anterior sólo
 * cambia cuál está activa: nada se borra nunca.
 */
export default function VersionesPresupuesto({ proyectoId, token, onCambio }) {
  const [abierto, setAbierto] = useState(false);
  const [versiones, setVersiones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  const actual = versiones.find((v) => v.es_actual);

  const cargar = useCallback(() => {
    if (!proyectoId) return;
    setCargando(true);
    PresupuestoVersionesGet(proyectoId, token)
      .then((res) => setVersiones(res?.data ?? []))
      .finally(() => setCargando(false));
  }, [proyectoId, token]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const crearVersion = async () => {
    const siguiente = (versiones[0]?.numero ?? 0) + 1;
    const { value: nombre, isConfirmed } = await Swal.fire({
      title: "Crear nueva versión",
      html:
        "Se copian todas las cuentas, subcuentas y conceptos de la versión " +
        "actual a una versión nueva, que queda como la activa.<br><br>" +
        "La versión de ahorita no se pierde: se guarda tal cual, con su " +
        "historial de fechas de pago y facturas.",
      input: "text",
      inputPlaceholder: `Versión ${siguiente}`,
      showCancelButton: true,
      confirmButtonText: "Crear versión",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#2f3a34",
    });

    if (!isConfirmed) return;

    setTrabajando(true);
    try {
      const respuesta = await PresupuestoVersionCrear(
        { proyectos_id: proyectoId, nombre: nombre || undefined },
        token
      );

      if (!respuesta?.success) {
        throw new Error(respuesta?.message || "No se pudo crear la versión");
      }

      await cargar();
      if (onCambio) onCambio();
      Swal.fire({
        icon: "success",
        title: "Versión creada",
        text: `Ya estás trabajando en "${respuesta.data?.nombre}".`,
        confirmButtonColor: "#2f3a34",
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo crear la versión", text: error.message });
    } finally {
      setTrabajando(false);
    }
  };

  const usarVersion = async (version) => {
    const confirmacion = await Swal.fire({
      icon: "warning",
      title: `¿Usar "${version.nombre}"?`,
      text:
        "Esta versión pasa a ser la que se ve y se edita en el presupuesto. " +
        "No se borra nada: la versión en la que estás ahorita se queda guardada.",
      showCancelButton: true,
      confirmButtonText: "Sí, usar esta versión",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#2f3a34",
    });

    if (!confirmacion.isConfirmed) return;

    setTrabajando(true);
    try {
      const respuesta = await PresupuestoVersionRestaurar(
        { proyectos_id: proyectoId, version_id: version.id },
        token
      );

      if (!respuesta?.success) {
        throw new Error(respuesta?.message || "No se pudo cambiar de versión");
      }

      await cargar();
      if (onCambio) onCambio();
      setAbierto(false);
      Swal.fire({ icon: "success", title: "Versión activada", confirmButtonColor: "#2f3a34" });
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo cambiar de versión", text: error.message });
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="crud-secondary-btn"
        onClick={() => setAbierto(true)}
        title="Ver e historial de versiones del presupuesto"
      >
        <i className="bi bi-clock-history" aria-hidden="true"></i>
        {actual ? actual.nombre : "Versiones"}
      </button>

      <Modal size="lg" isOpen={abierto} toggle={() => setAbierto(false)}>
        <ModalHeader className="backend_titulo" toggle={() => setAbierto(false)}>
          Versiones del presupuesto
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="crud-add-btn"
              onClick={crearVersion}
              disabled={trabajando || cargando}
            >
              {trabajando ? (
                <span className="spinner-boton" aria-hidden="true" />
              ) : (
                <i className="bi bi-plus-lg" aria-hidden="true"></i>
              )}
              Crear nueva versión (copia de la actual)
            </button>
          </div>

          {cargando ? (
            <p className="text-muted mb-0">Cargando versiones...</p>
          ) : (
            <table className="table table-sm table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Versión</th>
                  <th>Creada</th>
                  <th>Por</th>
                  <th>Cuentas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {versiones.map((version) => (
                  <tr key={version.id} className={version.es_actual ? "table-success" : ""}>
                    <td>
                      {version.nombre}
                      {version.es_actual && <span className="badge bg-success ms-2">Actual</span>}
                    </td>
                    <td>
                      {version.created_at
                        ? new Date(version.created_at).toLocaleString("es-MX")
                        : "—"}
                    </td>
                    <td>{version.creado_por_nombre || "—"}</td>
                    <td>{version.cuentas}</td>
                    <td className="text-end">
                      {!version.es_actual && (
                        <button
                          type="button"
                          className="btn btn-outline-dark btn-sm"
                          onClick={() => usarVersion(version)}
                          disabled={trabajando}
                        >
                          Usar esta versión
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
